import { useCallback, useMemo } from "react"
import { useField } from "formik"
import { EventClickArg } from "@fullcalendar/core"
import { fetchFreeBusyScheduleForServiceCenter, FreeBusyScheduleResponse } from "../../../createOrderApi"
import { ResourceDetailsDialog } from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardTimelineCalendar } from "../calendar/StandardTimelineCalendar"
import { ResourceCalendarLabelContent } from "./ResourceCalendarLabelContent"
import { ResourceCalendarGroupContent } from "./ResourceCalendarGroupContent"
import { ResourceBookingPreviewImage } from "./ResourceBookingPreviewImage"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useResourceDetailsDialog } from "./useResourceDetailsDialog"
import { usePreviewImageDialog } from "./usePreviewImageDialog"
import { generateCalendarEvents } from "../calendar/helpers"
import { ResourceItem } from "../../../createOrderTypes"
import { createNewEvent } from "./helpers"
import {
    NullableCalendarEvent,
    TimelineCalendarEvent,
    TimelineCalendarResource,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
} from "../calendar/fullCalendarTypes"

const INTERVAL_MINUTES = 15

const transformData = (data: FreeBusyScheduleResponse) => {
    return data?.resources || []
}

type WeeklyResourceBookingCalendarProps = {
    timelineResources: TimelineCalendarResource[]
    searchRegex: RegExp | undefined
    groupImagesMap: Map<string, string>
    handleBookClick: () => void
}

export function WeeklyResourceBookingCalendar({
    timelineResources,
    searchRegex,
    groupImagesMap,
    handleBookClick,
}: WeeklyResourceBookingCalendarProps) {
    const dateTimeFactory = useDateTime()
    const { selectedImage, openImageModal, closeImageModal } = usePreviewImageDialog()
    const { resourceDetails, openResourceDetailsDialog, closeResourceDetailsDialog } = useResourceDetailsDialog()

    const [, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")
    const { mutateAsync: callEventsMutation } = useApiMutation(fetchFreeBusyScheduleForServiceCenter, {})

    const createEventSourceFetcher = useCallback(
        (serviceCenterUid: string) =>
            async (
                fetchInfo: { startStr: string; endStr: string },
                successCallback: (events: TimelineCalendarEvent[]) => void,
                failureCallback: (error: any) => void,
            ) => {
                try {
                    const result = await callEventsMutation({
                        serviceCenterUid,
                        availabilityViewInterval: INTERVAL_MINUTES,
                        startTime: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromISO(fetchInfo.startStr)),
                        endTime: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromISO(fetchInfo.endStr)),
                    })

                    const slots = transformData(result)
                    const events = generateCalendarEvents(slots, INTERVAL_MINUTES, dateTimeFactory)

                    successCallback(events)
                } catch (err) {
                    failureCallback(err)
                }
            },
        [callEventsMutation, dateTimeFactory],
    )

    const eventSources = useMemo(() => {
        const uniqueServiceCenterUidList = [...new Set(timelineResources.map((r) => r.serviceCenterUid))]

        return uniqueServiceCenterUidList.map((uid) => ({
            id: `source-${uid}`,
            events: createEventSourceFetcher(uid),
        }))
    }, [timelineResources, createEventSourceFetcher])

    const handleChangeLocalEvent = (event: NullableCalendarEvent) => {
        eventHelpers.setValue(event)

        if (!event) {
            fullResourcesHelpers.setValue([])
            return
        }
        const fromTimeline = timelineResources
            .filter((res) => event.resourceIds.includes(res.id))
            .map((res) => ({
                ...res,
                id: Number(res.id),
            }))
        fullResourcesHelpers.setValue(fromTimeline)
    }

    const handleEventClick = (info: EventClickArg) => {
        const event = info.event
        const resourceId = event._def.resourceIds?.[0]

        if (resourceId) {
            const startTime = dateTimeFactory.fromISO(event.startStr)
            const endTime = dateTimeFactory.fromISO(event.endStr)

            const adjustedEndTime =
                endTime.diff(startTime, "hours").hours > 1 ? startTime.plus({ hours: 1 }).toISO() : event.endStr

            handleChangeLocalEvent(
                createNewEvent(resourceId, event.startStr, adjustedEndTime, {
                    start: event.startStr,
                    end: event.endStr,
                }),
            )
            handleBookClick()
        }
    }

    const resourceLabelContent = (resourceContent: TimelineResourceLabelContent) => (
        <ResourceCalendarLabelContent
            resourceContent={resourceContent}
            searchRegex={searchRegex}
            handleOpenDetails={openResourceDetailsDialog}
        />
    )

    const resourceGroupLabelContent = (resourceContent: TimelineResourceGroupLabelContent) => (
        <ResourceCalendarGroupContent
            resourceContent={resourceContent}
            groupImagesMap={groupImagesMap}
            onImageClick={openImageModal}
        />
    )

    return (
        <>
            <StandardTimelineCalendar
                viewMode="weekGrid"
                eventSources={eventSources}
                resources={timelineResources}
                resourceLabelContent={resourceLabelContent}
                resourceGroupLabelContent={resourceGroupLabelContent}
                handleEventClick={handleEventClick}
            />
            {selectedImage && (
                <ResourceBookingPreviewImage previewImage={selectedImage} handleClose={closeImageModal} />
            )}
            {resourceDetails && (
                <ResourceDetailsDialog resourceDetails={resourceDetails} handleClose={closeResourceDetailsDialog} />
            )}
        </>
    )
}
